import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import ActivityInput from 'components/shared/activityInput/ActivityInput';
import useActivityInput from 'components/shared/activityInput/useActivityInput';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import AttachmentComponent from 'components/shared/ExternalAttachment';
import { TaggedText } from 'components/TaggedText';
import { ActivityType } from 'models/Activity';
import Escalation from 'models/Escalation';
import { EscalationOptionTypes } from 'models/Escalation';
import Profile from 'models/Profile';
import { createActivity } from 'services/api/activity';
import { attachmentsQueryKeys } from 'services/api/attachments';
import { useAcknowledgeEscalation } from 'services/api/escalation';
import { useTaskModalActions } from 'stores/taskModalStore';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall, Label, LabelBold } from 'styles/typography';
import CheckIcon from 'svg/CheckIcon';

import TaskModalNotification, { NotificationTypes } from './TaskModalNotification';

type TaskModalBodyType = {
  record?: Escalation;
  profile: Profile;
  closeModal: () => void;
  loading: boolean;
  viewType: EscalationOptionTypes;
};

export default function TaskModalBody(props: TaskModalBodyType) {
  const { closeModal, record, profile, loading, viewType } = props;

  const queryClient = useQueryClient();
  const { mutate, isPending: updatingEscalation } = useAcknowledgeEscalation();
  const navigate = useNavigate();
  const { addToast } = useToastActions();
  const { addViewedEpisode } = useTaskModalActions();

  const [displayNotificationType, setDisplayNotificationType] = useState<NotificationTypes | null>(null);

  const { note, setNote, resetNote, getSerializedNoteValues } = useActivityInput();

  const { mutate: mutateActivity, isPending: creatingActivity } = useMutation({
    mutationFn: createActivity,
  });

  if (loading) {
    return (
      <BodyContainer>
        <StyledCircularProgress color={colors.blue100} />
      </BodyContainer>
    );
  }

  if (!record) {
    return (
      <EmptyState>
        <Emoji>🎉</Emoji>
        <EmptyStateTitle>You're all caught up!</EmptyStateTitle>
        <BodySmall>
          No {viewType === EscalationOptionTypes.Escalation ? 'Escalations' : 'Priority Notes'} to take care of right
          now. Check back soon!
        </BodySmall>
      </EmptyState>
    );
  }

  const recordType = record.typeForProfile(profile);

  const activity = record.activity!;
  const { summary, patient } = record;

  const latestRehabFacilityName = record.episode.latestLocationEpisode?.group?.name;
  const ownerName = record.episode.owner?.name;

  const recordCanBeAcknowledged = record.userCanAcknowledge(profile);

  const submitDisabled = record.acknowledged;

  const acknowledge = async () => {
    mutate(
      {
        id: record.id,
      },
      {
        onSuccess: () => {
          setDisplayNotificationType(NotificationTypes.ACKNOWLEDGED);
        },
      }
    );
  };

  const handleSubmitNote = async () => {
    mutateActivity(
      {
        ...getSerializedNoteValues(),
        type: ActivityType.NOTE,
        locationEpisodeId: record.locationEpisode?.id,
      },
      {
        onSuccess: async () => {
          queryClient.invalidateQueries({
            queryKey: attachmentsQueryKeys.count(),
          });
          resetNote();
          if (!record.acknowledged) {
            await acknowledge();
          } else {
            setDisplayNotificationType(NotificationTypes.NOTE_ADDED);
          }
        },
        onError: () => {
          addToast({ text: 'Something went wrong. Please try again.' });
        },
      }
    );
  };

  const viewPatientStory = () => {
    addViewedEpisode(record.episode.id);
    navigate(`/patients/${record.episode.id}`);
    closeModal();
  };

  return (
    <BodyContainer>
      <HeadContainer>
        <Details>
          <b>{patient.name}</b>
          <GroupDetails>
            {ownerName} — {latestRehabFacilityName}
          </GroupDetails>
        </Details>

        <PatientStoryLink onClick={viewPatientStory} role='link'>
          Go to patient story
        </PatientStoryLink>

        {recordCanBeAcknowledged && (
          <AcknowledgeButton size='xsmall' onClick={acknowledge} disabled={submitDisabled} loading={updatingEscalation}>
            {!updatingEscalation && <CheckIcon width={12} height={12} />}
            Acknowledge
          </AcknowledgeButton>
        )}
      </HeadContainer>
      <BodyInnerContainer>
        <LabelBold>{summary}</LabelBold>
        <TaggedText text={activity.data.text ?? ''} $archived={false} />
        {!!activity.attachments?.length && (
          <AttachmentsContainer>
            {activity.attachments?.map((attachment) => (
              <AttachmentComponent disabled={activity.archived} key={attachment.id} attachment={attachment} />
            ))}
          </AttachmentsContainer>
        )}
      </BodyInnerContainer>
      {recordCanBeAcknowledged && (
        <FooterContainer>
          <TaskModalNotification
            displayNotificationType={displayNotificationType}
            recordType={recordType}
            setDisplayNotificationType={setDisplayNotificationType}
            selectedRecord={record}
          />
          <ActivityInput
            values={note}
            setValues={setNote}
            onSubmit={handleSubmitNote}
            showPostButton
            heightSettings={{
              minHeight: '50px',
              maxHeight: '150px',
            }}
            placeholder={'Adding a reply to this note will also acknowledge it'}
            submitting={creatingActivity}
            locationEpisodeId={record.locationEpisode?.id}
          />
        </FooterContainer>
      )}
    </BodyContainer>
  );
}

const BodyContainer = styled.div`
  display: flex;
  flex-basis: 65%;
  flex-shrink: 1;
  flex-direction: column;
  min-width: 0;
  justify-content: center;
`;

const StyledCircularProgress = styled(CircularProgress)`
  align-self: center;
`;

const BodyInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 24px;
  margin-bottom: 16px;
  overflow-y: scroll;
  height: 100px;
  flex-grow: 1;
  gap: 24px;

  &::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${colors.black50};
    border-radius: 5pt;
  }
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: auto 24px 24px 24px;
`;

const HeadContainer = styled.div`
  border-bottom: 1px solid ${colors.black10};
  display: flex;
  padding: 16px 24px;
  min-width: 0;
  align-items: center;
`;

const GroupDetails = styled(Label)`
  color: ${colors.black75};
  margin-top: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-right: 24px;
  min-width: 0;
`;

const AcknowledgeButton = styled(Button)`
  padding: 8px 10px;
  display: flex;
  gap: 8px;
  height: 34px;
  margin-left: 16px;
`;

const PatientStoryLink = styled.p`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.black100};
  font-size: 12px;
  margin: 0;
  cursor: pointer;
`;

const AttachmentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyState = styled.div`
  height: 100%;
  flex: 1;
  justify-content: center;
  padding: 0 16px;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 14px;
`;

const Emoji = styled.p`
  font-size: 36px;
  line-height: 40px;
`;

const EmptyStateTitle = styled.p`
  font-size: 20px;
  font-weight: 500;
`;
